/**
 * Marketing copy for the public site, lifted from the design mockups
 * ("FenceAuth Public Site", "FenceAuth Pricing Page"). Kept as typed content
 * modules — the localization boundary for these dense marketing/matrix blocks;
 * when a second locale lands they move into namespaced i18n catalogs wholesale.
 */

/** Per-slug presentation for plan cards — the API stays the source of truth
 * for name/price/trial; this adds the marketing framing per plan. */
export interface PlanPresentation {
  blurb: string
  bullets: { label: string; included: boolean; strong?: boolean }[]
  ctaNote?: string
}

export const PLAN_PRESENTATION: Record<string, PlanPresentation> = {
  starter: {
    blurb: 'One product, your first paying customers.',
    bullets: [
      { label: '1,000 monthly active users', included: true, strong: true },
      { label: '1 product · 2 team seats', included: true },
      { label: 'Hosted portal on our domain', included: true },
      { label: 'Webhooks & API keys', included: true },
      { label: 'SSO / SCIM', included: false },
    ],
  },
  growth: {
    blurb: 'Multiple products, first enterprise deals.',
    bullets: [
      { label: '25,000 monthly active users', included: true, strong: true },
      { label: '5 products · 10 team seats', included: true },
      { label: 'SSO (OIDC + SAML) & SCIM', included: true, strong: true },
      { label: 'Custom domain portal & SMTP', included: true },
      { label: '99.9% SLA · email support', included: true },
    ],
    ctaNote: '14 days free · no card',
  },
  scale: {
    blurb: 'High volume with a tighter SLA.',
    bullets: [
      { label: '100,000 monthly active users', included: true, strong: true },
      { label: '20 products · 50 team seats', included: true },
      { label: 'Sandbox tenants & test mode', included: true },
      { label: '99.95% SLA · priority support', included: true },
      { label: 'Quarterly architecture review', included: true },
    ],
  },
}

/** Enterprise renders as a static dark card — it is not a self-serve catalog plan. */
export const ENTERPRISE_CARD = {
  name: 'Enterprise',
  priceLabel: 'Custom',
  priceNote: 'annual contract · net 30',
  blurb: 'Residency, procurement and a name to call.',
  bullets: [
    'Unlimited MAU, products, seats',
    'EU or US data residency',
    'Custom DPA, BAA, pen-test report',
    '99.99% SLA · dedicated CSM',
    'Private egress IPs & outbox',
  ],
  ctaLabel: 'Contact sales',
  ctaNote: 'Security review in 5 days',
}

/** The plan the pricing page nudges undecided visitors toward. */
export const RECOMMENDED_PLAN_SLUG = 'growth'

export interface FaqEntry {
  question: string
  answer: string
}

export const PRICING_FAQ: FaqEntry[] = [
  {
    question: 'What counts as a monthly active user?',
    answer:
      "Any end-user who authenticates at least once in the billing period. Deleted, never-verified and suspended users don't count, and your own team seats are billed separately from MAU.",
  },
  {
    question: 'What happens the month we exceed the cap?',
    answer:
      'We email you at 80% and 100%. Past the cap, new end-user registrations return a quota error while everything already live keeps working — we never auto-upgrade you or send a surprise invoice.',
  },
  {
    question: 'Can we change plans mid-cycle?',
    answer:
      'Yes. Upgrades take effect immediately with a prorated charge; downgrades show you the prorated credit before you confirm and apply at your choice of now or period end.',
  },
  {
    question: 'Who is the merchant of record for our customers?',
    answer:
      'You are. FenceAuth orchestrates subscriptions and entitlements against your own Stripe account — your customers see your name on their statement and you keep the direct relationship.',
  },
  {
    question: 'Do you take a percentage of our revenue?',
    answer:
      'No. Plans are flat monthly fees based on monthly active users. We never take a cut of what your customers pay you.',
  },
  {
    question: 'What happens to our data if we leave?',
    answer:
      'You can export users, subscriptions, invoices and the full audit log as NDJSON at any time — including after cancellation, free of charge, for 90 days.',
  },
]

type MatrixValue = boolean | string | null

export interface MatrixRow {
  capability: string
  values: [MatrixValue, MatrixValue, MatrixValue, MatrixValue] // starter, growth, scale, enterprise
}

export interface MatrixSection {
  title: string
  rows: MatrixRow[]
}

/** Full comparison matrix per the pricing mockup — numbers where numbers exist. */
export const COMPARISON_MATRIX: MatrixSection[] = [
  {
    title: 'Limits',
    rows: [
      { capability: 'Monthly active users', values: ['1,000', '25,000', '100,000', 'unlimited'] },
      { capability: 'Products', values: ['1', '5', '20', 'unlimited'] },
      { capability: 'Team seats (your staff)', values: ['2', '10', '50', 'unlimited'] },
      { capability: 'API requests / month', values: ['1M', '10M', '50M', 'custom'] },
    ],
  },
  {
    title: 'Identity',
    rows: [
      { capability: 'Email + password, MFA (TOTP)', values: [true, true, true, true] },
      { capability: 'Enterprise SSO — OIDC & SAML', values: [null, true, true, true] },
      { capability: 'SCIM 2.0 provisioning & groups', values: [null, true, true, true] },
      { capability: 'Session policy & MFA enforcement', values: ['basic', true, true, true] },
    ],
  },
  {
    title: 'Billing & subscriptions',
    rows: [
      { capability: 'Trials, proration, dunning, refunds', values: [true, true, true, true] },
      {
        capability: 'Multi-currency prices per plan',
        values: ['1 currency', '5', '20', 'unlimited'],
      },
      { capability: 'Refund dual control & fraud signals', values: [null, true, true, true] },
      { capability: 'Disputes & evidence packages', values: [null, true, true, true] },
    ],
  },
  {
    title: 'Customer portal',
    rows: [
      { capability: 'Hosted register, billing & profile', values: [true, true, true, true] },
      { capability: 'Your logo & colours', values: [true, true, true, true] },
      { capability: 'Custom domain + your SMTP', values: [null, true, true, true] },
      { capability: 'Remove "Powered by FenceAuth"', values: [null, true, true, true] },
    ],
  },
  {
    title: 'Operations, compliance & support',
    rows: [
      { capability: 'Webhooks + delivery log', values: ['7 days', '30 days', '90 days', 'custom'] },
      { capability: 'Audit log retention', values: ['1 year', '7 years', '7 years', 'custom'] },
      { capability: 'Data residency choice', values: [null, null, 'EU or US', true] },
      { capability: 'Uptime SLA', values: ['none', '99.9%', '99.95%', '99.99%'] },
      {
        capability: 'Support',
        values: ['community', 'email · 1 day', 'priority · 4 h', 'CSM · 1 h'],
      },
    ],
  },
]

export const NEVER_CHARGED = [
  'API calls',
  'Webhook deliveries',
  'Inactive or deleted users',
  'Sandbox tenants',
  'Support tickets',
  'Data export on exit',
]

export const PROCUREMENT_PACK = [
  'SOC 2 Type II report',
  'DPA + SCCs',
  'Pen-test summary',
  'Sub-processor list',
  'Uptime history',
  'W-9 / VAT details',
]
